/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CustomScrollbar
 *
 * @file CustomScrollbar.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import mixins from '../../assets/styles/mixins.scss';

const ScrollBarContainer = styled(Scrollbars, {
  shouldForwardProp: (prop) => prop !== 'enableScrollPadding' && prop !== 'withFlex'
})(({ enableScrollPadding, withFlex }) => ({
  '&&': {
    height: '100%',

    '& > div': {
      scrollbarColor: `${mixins.quaternary} transparent`,
      ...(withFlex ? {
        display: 'flex',
        flexDirection: 'column'
      } : {}),
      ...(enableScrollPadding ? {
        '&:first-child > *': {
          paddingRight: 4,
          paddingBottom: 4
        }
      } : {})
    }
  }
}));

const VerticalThumb = styled('div')({
  '&&': {
    boxSizing: 'border-box',
    backgroundColor: mixins.quaternary,
    borderRadius: 4,
    zIndex: 999
  }
});

const CustomScrollbar = (props) => {
  const {
    children,
    showScrollbar = true,
    enableScrollPadding = true,
    withFlex = false,
    className = '',
    ...scrollbarProps
  } = props;

  if (!showScrollbar) {
    return children;
  }

  return (
    <ScrollBarContainer
      className={className}
      enableScrollPadding={enableScrollPadding}
      withFlex={withFlex}
      autoHide
      autoHideTimeout={1000}
      autoHideDuration={200}
      renderThumbVertical={scrollProps => <VerticalThumb {...scrollProps} />}
      {...scrollbarProps}
    >
      {children}
    </ScrollBarContainer>
  );
};

CustomScrollbar.propTypes = {
  children: PropTypes.node.isRequired,
  showScrollbar: PropTypes.bool,
  enableScrollPadding: PropTypes.bool,
  withFlex: PropTypes.bool,
  className: PropTypes.string
};

CustomScrollbar.defaultProps = {
  showScrollbar: true,
  enableScrollPadding: true,
  withFlex: false,
  className: ''
};


export default CustomScrollbar;
